/**
 * When adding to this list, please also
 *  a) add a label
 *  b) define specific ordering so that the cap markets dashboard and credit
 *     summary hurdle update.
 *
 * For a complete list of active rate adjusters see:
 * https://github.com/homelight/eave-go/blob/main/underwriting/rate_adjuster.go
 */

export enum RateAdjusters {
  AutoExperienceFirstTimeBuyer = "auto_experience_first_time_buyer",
  Bankruptcy = "bankruptcy",
  ClampTotalBetweenMinAndMaxInclusive = "clamp_total_between_min_and_max_inclusive",
  CondoAbove75percent = "condo_above_75_percent",
  CondoAtOrBelow75percent = "condo_at_or_below_75_percent",
  CondoOrSecondary = "condo_or_secondary",
  Conforming = "conforming",
  Conforming30YrFixed = "conforming_30yr_fixed",
  Conforming51ARM = "conforming_5_1_arm",
  Conforming71ARM = "conforming_7_1_arm",
  EmploymentNonStability = "employment_non_stability",
  ExtraDownpaymentTier1 = "extra_downpayment_tier1",
  ExtraDownpaymentTier2 = "extra_downpayment_tier2",
  FICOScore = "fico_score",
  FICOScore0To659 = "fico_score_0_to_659",
  FICOScore660To679 = "fico_score_660_to_679",
  FICOScore680To699 = "fico_score_680_to_699",
  FICOScore700To719 = "fico_score_700_to_719",
  FICOScore720To739 = "fico_score_720_to_739",
  FICOScore740To759 = "fico_score_740_to_759",
  FirstTimeHomebuyer = "first_time_homebuyer",
  FixedAndFlipped = "fixed_and_flipped",
  HighBalance = "high_balance",
  HighBalance30YrFixed = "high_balance_30yr_fixed",
  HighBalance51ARM = "high_balance_5_1_arm",
  HighBalance71ARM = "high_balance_7_1_arm",
  Inquiries = "inquiries",
  JudicialState = "judicial_state",
  LowDownpaymentConforming = "low_downpayment_conforming",
  LowDownpaymentHighBalance = "low_downpayment_high_balance",
  LowDownpaymentJumbo = "low_downpayment_jumbo",
  MinorCollectionsDelinquencies = "minor_collections_delinquencies",
  MortgageDelinquency = "mortgage_delinquency",
  MultiFamily24OrInvestmentHomes = "multi_family_2_4_or_investment_homes",
  NonMinorDelinquencies = "non_minor_delinquencies",
  Occupancy1Unit = "occupancy_1_unit",
  Occupancy2Unit = "occupancy_2_unit",
  Occupancy34Unit = "occupancy_3_4_unit",
  Promotion = "promotion",
  RevolvingUtilization = "revolving_utilization",
  SeriousMortgageDelinquency = "serious_mortgage_delinquency",
  TaxAndHazardNotEscrowed = "tax_and_hazard_not_escrowed",
  TenPercentDownpayment = "ten_percent_downpayment",
  UsageInvestment = "usage_investment",
  UsagePrimary = "usage_primary",
  UsageSecondary = "usage_secondary",
  UsageSecondaryAtOrBelow60 = "usage_secondary_at_or_below_60",
  UsageSecondaryAtOrBelow70 = "usage_secondary_at_or_below_70",
  UsageSecondaryAtOrBelow75 = "usage_secondary_at_or_below_75",
  UsageSecondaryAtOrBelow80 = "usage_secondary_at_or_below_80",
  UsageSecondaryAtOrBelow85 = "usage_secondary_at_or_below_85",
  UsageSecondaryAtOrBelow90 = "usage_secondary_at_or_below_90",
  UsageSecondaryAbove90 = "usage_secondary_above_90",
  LoanAmountEqualOrAbove1M = "loan_amount_equal_or_above_1m",
  LoanAmountEqualOrAbove1Dot25M = "loan_amount_equal_or_above_1.25m",
  LoanAmountEqualOrAbove1Dot5M = "loan_amount_equal_or_above_1.5m",
  LoanAmountEqualOrAbove2M = "loan_amount_equal_or_above_2m",
  LoanAmountEqualOrAbove3M = "loan_amount_equal_or_above_3m",
  LTVUsageSecondary = "ltv_usage_secondary",
  LTVOccupancy1Unit = "ltv_occupancy_1_unit",
  LTVOccupancy2Unit = "ltv_occupancy_2_unit",
  LTVOccupancy34Unit = "ltv_occupancy_3_4_unit",
  StateArizona = "state_az",
  StateCalifornia = "state_ca",
  StateColorado = "state_co",
  StateFlorida = "state_fl",
  StateOregon = "state_or",
  StatePennsylvania = "state_pa",
  StateTennessee = "state_tn",
  StateTexas = "state_tx",
  StateWashington = "state_wa",
}

export const dynamicFicoXLtvKey = (adj: RateAdjusters): string | null => {
  const m = adj.match(/fico_x_ltv\{(.*)\}/); // e.g., "fico_x_ltv{FICO 740 - 850 and LTV 0.00 - 60.00%}"
  if (m && m.length === 2) {
    return m[1]; // => "FICO 740 - 850 and LTV 0.00 - 60.00%"
  }
  return null;
};

// RateAdjusterLabels are shared between the capital markets dashboard and the
// credit summary hurdle.
const RateAdjusterLabels: { [key in RateAdjusters]: string } = {
  [RateAdjusters.AutoExperienceFirstTimeBuyer]: "First time homebuyer and No Clean Auto experience",
  [RateAdjusters.Bankruptcy]: "Ever Bankruptcy",
  [RateAdjusters.ClampTotalBetweenMinAndMaxInclusive]:
    "Adjustment to keep total between -4 and 20 (inclusive)",
  [RateAdjusters.CondoAbove75percent]: "Condo (LTV 75.01% or more)",
  [RateAdjusters.CondoAtOrBelow75percent]: "Condo (LTV 75.00% or less)",
  [RateAdjusters.CondoOrSecondary]: "Secondary, condo, or AirBnB Residence?",
  [RateAdjusters.Conforming]: "Conforming",
  [RateAdjusters.Conforming30YrFixed]: "Conforming 30-year fixed",
  [RateAdjusters.Conforming51ARM]: "Conforming 5/1 ARM",
  [RateAdjusters.Conforming71ARM]: "Conforming 7/1 ARM",
  [RateAdjusters.EmploymentNonStability]: "Employment Non-Stability",
  [RateAdjusters.ExtraDownpaymentTier1]: "Jumbo LTV 60.01% - 70.00%",
  [RateAdjusters.ExtraDownpaymentTier2]: "Jumbo LTV <= 60.00%",
  [RateAdjusters.FICOScore]: "FICO",
  [RateAdjusters.FICOScore0To659]: "FICO Score - 0 to 659",
  [RateAdjusters.FICOScore660To679]: "FICO Score - 660 to 679",
  [RateAdjusters.FICOScore680To699]: "FICO Score - 680 to 699",
  [RateAdjusters.FICOScore700To719]: "FICO Score - 700 to 719",
  [RateAdjusters.FICOScore720To739]: "FICO Score - 720 to 739",
  [RateAdjusters.FICOScore740To759]: "FICO Score - 740 to 759",
  [RateAdjusters.FirstTimeHomebuyer]: "FTHB (LTV 95.01% - 97.00%)",
  [RateAdjusters.FixedAndFlipped]: "Fixed & Flipped property being bought?",
  [RateAdjusters.HighBalance]: "High-balance",
  [RateAdjusters.HighBalance30YrFixed]: "High-balance 30-year fixed",
  [RateAdjusters.HighBalance51ARM]: "High-balance 5/1 ARM",
  [RateAdjusters.HighBalance71ARM]: "High-balance 7/1 ARM",
  [RateAdjusters.Inquiries]: "Recent Credit Hunger (Inquiries)",
  [RateAdjusters.JudicialState]: "Judicial state",
  [RateAdjusters.LowDownpaymentConforming]: "Conforming LTV 80.01% - 95.00%",
  [RateAdjusters.LowDownpaymentHighBalance]: "High Balance LTV 80.01% - 95.00%",
  [RateAdjusters.LowDownpaymentJumbo]: "Jumbo LTV 80.01% - 89.90%",
  [RateAdjusters.MinorCollectionsDelinquencies]:
    "Minor collections & delinquencies in last 12 months",
  [RateAdjusters.MortgageDelinquency]: "1 Mortgage 30+ days late",
  [RateAdjusters.MultiFamily24OrInvestmentHomes]: "Multi family (2-4) or investment homes",
  [RateAdjusters.NonMinorDelinquencies]: "Presence of DQs on $300+ payment obligations in L12",
  [RateAdjusters.Occupancy1Unit]: "Number of Units: 1",
  [RateAdjusters.Occupancy2Unit]: "Number of Units: 2",
  [RateAdjusters.Occupancy34Unit]: "Number of Units: 3-4",
  [RateAdjusters.Promotion]: "Promotion",
  [RateAdjusters.RevolvingUtilization]: "Revolving Utilization > 80%",
  [RateAdjusters.SeriousMortgageDelinquency]: "Ever 90+ days late on Mortgages",
  [RateAdjusters.TaxAndHazardNotEscrowed]: "Taxes and Insurance Not Escrowed",
  [RateAdjusters.TenPercentDownpayment]: "Ten percent downpayment program",
  [RateAdjusters.UsageInvestment]: "Investment Property",
  [RateAdjusters.UsagePrimary]: "Primary Home",
  [RateAdjusters.UsageSecondary]: "Second Home",
  [RateAdjusters.UsageSecondaryAtOrBelow60]: "Second Home <= 60% LTV",
  [RateAdjusters.UsageSecondaryAtOrBelow70]: "Second Home <= 70% LTV",
  [RateAdjusters.UsageSecondaryAtOrBelow75]: "Second Home <= 75% LTV",
  [RateAdjusters.UsageSecondaryAtOrBelow80]: "Second Home <= 80% LTV",
  [RateAdjusters.UsageSecondaryAtOrBelow85]: "Second Home <= 85% LTV",
  [RateAdjusters.UsageSecondaryAtOrBelow90]: "Second Home <= 90% LTV",
  [RateAdjusters.UsageSecondaryAbove90]: "Second Home > 90% LTV",
  [RateAdjusters.LoanAmountEqualOrAbove1M]: "Loan Amount >= 1 million",
  [RateAdjusters.LoanAmountEqualOrAbove1Dot25M]: "Loan Amount >= 1.25 million",
  [RateAdjusters.LoanAmountEqualOrAbove1Dot5M]: "Loan Amount >= 1.5 million",
  [RateAdjusters.LoanAmountEqualOrAbove2M]: "Loan Amount >= 2 million",
  [RateAdjusters.LoanAmountEqualOrAbove3M]: "Loan Amount >= 3 million",
  [RateAdjusters.LTVUsageSecondary]: "Second Home",
  [RateAdjusters.LTVOccupancy1Unit]: "Number of Units: 1",
  [RateAdjusters.LTVOccupancy2Unit]: "Number of Units: 2",
  [RateAdjusters.LTVOccupancy34Unit]: "Number of Units: 3-4",
  [RateAdjusters.StateArizona]: "State AZ",
  [RateAdjusters.StateCalifornia]: "State CA",
  [RateAdjusters.StateColorado]: "State CO",
  [RateAdjusters.StateFlorida]: "State FL",
  [RateAdjusters.StateOregon]: "State OR",
  [RateAdjusters.StatePennsylvania]: "State PA",
  [RateAdjusters.StateTennessee]: "State TN",
  [RateAdjusters.StateTexas]: "State TX",
  [RateAdjusters.StateWashington]: "State WA",
};

export const rateAdjusterToLabel = (adj: RateAdjusters): string =>
  dynamicFicoXLtvKey(adj) || RateAdjusterLabels[adj] || adj;

const OrderedRateAdjusters: RateAdjusters[] = [
  // current; in a particular order
  RateAdjusters.UsagePrimary,
  RateAdjusters.LTVOccupancy1Unit,
  RateAdjusters.LTVOccupancy2Unit,
  RateAdjusters.LTVOccupancy34Unit,
  RateAdjusters.LTVUsageSecondary,
  RateAdjusters.Occupancy1Unit,
  RateAdjusters.CondoAbove75percent,
  RateAdjusters.CondoAtOrBelow75percent,
  RateAdjusters.TaxAndHazardNotEscrowed,
  RateAdjusters.LoanAmountEqualOrAbove1M,
  RateAdjusters.LoanAmountEqualOrAbove1Dot25M,
  RateAdjusters.LoanAmountEqualOrAbove1Dot5M,
  RateAdjusters.LoanAmountEqualOrAbove2M,
  RateAdjusters.LoanAmountEqualOrAbove3M,
  RateAdjusters.Promotion,
  RateAdjusters.StateArizona,
  RateAdjusters.StateCalifornia,
  RateAdjusters.StateColorado,
  RateAdjusters.StateFlorida,
  RateAdjusters.StateOregon,
  RateAdjusters.StatePennsylvania,
  RateAdjusters.StateTennessee,
  RateAdjusters.StateTexas,
  RateAdjusters.StateWashington,

  // no longer in use; alphabetized (kept for credit summary hurdle display)
  RateAdjusters.AutoExperienceFirstTimeBuyer,
  RateAdjusters.Bankruptcy,
  RateAdjusters.ClampTotalBetweenMinAndMaxInclusive,
  RateAdjusters.CondoOrSecondary,
  RateAdjusters.Conforming,
  RateAdjusters.Conforming30YrFixed,
  RateAdjusters.Conforming51ARM,
  RateAdjusters.Conforming71ARM,
  RateAdjusters.EmploymentNonStability,
  RateAdjusters.ExtraDownpaymentTier1,
  RateAdjusters.ExtraDownpaymentTier2,
  RateAdjusters.FICOScore,
  RateAdjusters.FICOScore0To659,
  RateAdjusters.FICOScore660To679,
  RateAdjusters.FICOScore680To699,
  RateAdjusters.FICOScore700To719,
  RateAdjusters.FICOScore720To739,
  RateAdjusters.FICOScore740To759,
  RateAdjusters.FirstTimeHomebuyer,
  RateAdjusters.FixedAndFlipped,
  RateAdjusters.HighBalance,
  RateAdjusters.HighBalance30YrFixed,
  RateAdjusters.HighBalance51ARM,
  RateAdjusters.HighBalance71ARM,
  RateAdjusters.Inquiries,
  RateAdjusters.JudicialState,
  RateAdjusters.LowDownpaymentConforming,
  RateAdjusters.LowDownpaymentHighBalance,
  RateAdjusters.LowDownpaymentJumbo,
  RateAdjusters.MinorCollectionsDelinquencies,
  RateAdjusters.MortgageDelinquency,
  RateAdjusters.MultiFamily24OrInvestmentHomes,
  RateAdjusters.NonMinorDelinquencies,
  RateAdjusters.Occupancy2Unit,
  RateAdjusters.Occupancy34Unit,
  RateAdjusters.RevolvingUtilization,
  RateAdjusters.SeriousMortgageDelinquency,
  RateAdjusters.TenPercentDownpayment,
  RateAdjusters.UsageInvestment,
  RateAdjusters.UsageSecondary,
  RateAdjusters.UsageSecondaryAbove90,
  RateAdjusters.UsageSecondaryAtOrBelow60,
  RateAdjusters.UsageSecondaryAtOrBelow70,
  RateAdjusters.UsageSecondaryAtOrBelow75,
  RateAdjusters.UsageSecondaryAtOrBelow80,
  RateAdjusters.UsageSecondaryAtOrBelow85,
  RateAdjusters.UsageSecondaryAtOrBelow90,
];

export const rateAdjusterToRank = (adj: RateAdjusters): number => {
  if (dynamicFicoXLtvKey(adj)) {
    return -1; // dynamic keys first (-1 < index 0)
  }
  if (OrderedRateAdjusters.includes(adj)) {
    return OrderedRateAdjusters.indexOf(adj);
  }
  return Infinity; // so undefined/unknown adjusters rank last
};

const RateAdjusterTooltips: { [key in RateAdjusters]: string } = {
  [RateAdjusters.AutoExperienceFirstTimeBuyer]: "",
  [RateAdjusters.Bankruptcy]: "",
  [RateAdjusters.ClampTotalBetweenMinAndMaxInclusive]: "",
  [RateAdjusters.CondoAbove75percent]:
    "Applies when the subject property is any type of condo and where the LTV is 75.01% or more.",
  [RateAdjusters.CondoAtOrBelow75percent]:
    "Applies when the subject property is any type of condo and where the LTV is 75.00% or less.",
  [RateAdjusters.CondoOrSecondary]: "",
  [RateAdjusters.Conforming]: "",
  [RateAdjusters.Conforming30YrFixed]: "",
  [RateAdjusters.Conforming51ARM]: "",
  [RateAdjusters.Conforming71ARM]: "",
  [RateAdjusters.EmploymentNonStability]: "",
  [RateAdjusters.ExtraDownpaymentTier1]:
    'Applies to Jumbo loans where the LTV is 60.01% - 70.00%. Previously noted as borrower having an "extra downpayment of 10% more" than 20% (or restated a total downpayment of between 30.00% and 39.99%).',
  [RateAdjusters.ExtraDownpaymentTier2]:
    'Applies to Jumbo loans where the LTV is 60.00% or less. Previously noted as borrower having an "extra downpayment of 20% more" than 20% (or restated a total downpayment of 40.00% or more).',
  [RateAdjusters.FICOScore]: "",
  [RateAdjusters.FICOScore0To659]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FICOScore660To679]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FICOScore680To699]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FICOScore700To719]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FICOScore720To739]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FICOScore740To759]:
    "Applies according to the amount of the qualifying FICO score from a successful hard credit pull.",
  [RateAdjusters.FirstTimeHomebuyer]:
    "Applies if at least one borrower has not held title on any properties in the last 3 years AND the LTV is >95.00%.",
  [RateAdjusters.FixedAndFlipped]: "",
  [RateAdjusters.HighBalance]: "",
  [RateAdjusters.HighBalance30YrFixed]: "",
  [RateAdjusters.HighBalance51ARM]: "",
  [RateAdjusters.HighBalance71ARM]: "",
  [RateAdjusters.Inquiries]: "",
  [RateAdjusters.JudicialState]: "",
  [RateAdjusters.LowDownpaymentConforming]:
    'Applies to GSE loans with loan amounts at or below the conforming limit of $510,400.00 where the LTV is 80.01% - 95.00%. Previously called "low downpayment program".',
  [RateAdjusters.LowDownpaymentHighBalance]:
    'Applies to GSE loans with loan amounts above the conforming limit of $510,400.00 where the LTV is 80.01% - 95.00%. Previously called "low downpayment program".',
  [RateAdjusters.LowDownpaymentJumbo]:
    'Applies to Jumbo loans where the LTV is 80.01% - 89.90%. Previously called "low downpayment program".',
  [RateAdjusters.MinorCollectionsDelinquencies]: "",
  [RateAdjusters.MortgageDelinquency]: "",
  [RateAdjusters.MultiFamily24OrInvestmentHomes]: "",
  [RateAdjusters.NonMinorDelinquencies]: "",
  [RateAdjusters.Occupancy1Unit]: "Applies when the subject property is a one unit property.",
  [RateAdjusters.Occupancy2Unit]: "Applies when the subject property is a two unit property.",
  [RateAdjusters.Occupancy34Unit]:
    "Applies when the subject property is a three or four unit property.",
  [RateAdjusters.Promotion]:
    "Amount based on a predetermined promotion for a certain period of time. Set through Promo rate subtracter in LoanFileWorksheetHurdle.",
  [RateAdjusters.RevolvingUtilization]: "",
  [RateAdjusters.SeriousMortgageDelinquency]: "",
  [RateAdjusters.TaxAndHazardNotEscrowed]:
    "Applies when the property taxes and homeowners insurance impounds have been waived by both lender and borrower.",
  [RateAdjusters.TenPercentDownpayment]: "",
  [RateAdjusters.UsageInvestment]:
    "Applies when the subject property's occupancy is an investment property.",
  [RateAdjusters.UsagePrimary]: "Applies when the subject property's occupancy is primary.",
  [RateAdjusters.UsageSecondary]:
    "Applies when the subject property's occupancy is a second or vacation home.",
  [RateAdjusters.UsageSecondaryAtOrBelow60]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 60% LTV.",
  [RateAdjusters.UsageSecondaryAtOrBelow70]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 70% LTV.",
  [RateAdjusters.UsageSecondaryAtOrBelow75]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 75% LTV.",
  [RateAdjusters.UsageSecondaryAtOrBelow80]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 80% LTV.",
  [RateAdjusters.UsageSecondaryAtOrBelow85]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 85% LTV.",
  [RateAdjusters.UsageSecondaryAtOrBelow90]:
    "Applies when the subject property's occupancy is a second or vacation home at or below 90% LTV.",
  [RateAdjusters.UsageSecondaryAbove90]:
    "Applies when the subject property's occupancy is a second or vacation home above 90% LTV.",
  [RateAdjusters.LoanAmountEqualOrAbove1M]:
    "Applies when the Loan Amount is greater than or equal to 1 million with priority to the greatest loan amount adjuster",
  [RateAdjusters.LoanAmountEqualOrAbove1Dot25M]:
    "Applies when the Loan Amount is greater than or equal to 1.25 million with priority to the greatest loan amount adjuster",
  [RateAdjusters.LoanAmountEqualOrAbove1Dot5M]:
    "Applies when the Loan Amount is greater than or equal to 1.5 million with priority to the greatest loan amount adjuster",
  [RateAdjusters.LoanAmountEqualOrAbove2M]:
    "Applies when the Loan Amount is greater than or equal to 2 million with priority to the greatest loan amount adjuster",
  [RateAdjusters.LoanAmountEqualOrAbove3M]:
    "Applies when the Loan Amount is greater than or equal to 3 million with priority to the greatest loan amount adjuster",
  [RateAdjusters.LTVUsageSecondary]:
    "Applies when the subject property's occupancy is a second or vacation home",
  [RateAdjusters.LTVOccupancy1Unit]: "Applies when the subject property is a one unit property.",
  [RateAdjusters.LTVOccupancy2Unit]: "Applies when the subject property is a two unit property.",
  [RateAdjusters.LTVOccupancy34Unit]:
    "Applies when the subject property is a three or four unit property.",
  [RateAdjusters.StateArizona]: "Applies when property state is AZ.",
  [RateAdjusters.StateCalifornia]: "Applies when property state is CA.",
  [RateAdjusters.StateColorado]: "Applies when property state is CO.",
  [RateAdjusters.StateFlorida]: "Applies when property state is FL.",
  [RateAdjusters.StateOregon]: "Applies when property state is OR.",
  [RateAdjusters.StatePennsylvania]: "Applies when property state is PA.",
  [RateAdjusters.StateTennessee]: "Applies when property state is TN.",
  [RateAdjusters.StateTexas]: "Applies when property state is TX.",
  [RateAdjusters.StateWashington]: "Applies when property state is WA.",
};

const dynamicFicoXLtvKeyTooltip =
  "Applies where the value of the qualifying FICO score from a successful hard credit pull is in the displayed range AND the LTV is in the displayed range.";

export const rateAdjusterToTooltip = (adj: RateAdjusters): string => {
  if (dynamicFicoXLtvKey(adj)) {
    return dynamicFicoXLtvKeyTooltip;
  }
  return RateAdjusterTooltips[adj];
};
